import { useRef, useMemo, useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { UseTestReportReturn, PDFGenerationOptions } from '../../../types/certificate.ts';
import { generateReportId, generateReportUrl } from '../../../utils/validation.ts';

/**
 * 检测报告自定义Hook
 * 管理检测报告的基础功能和PDF生成
 */
export const useTestReport = (): UseTestReportReturn => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // 生成唯一的报告ID，只在Hook初始化时生成一次
  const reportId = useMemo(() => generateReportId(), []);
  
  // 生成报告访问URL
  const reportUrl = useMemo(() => generateReportUrl(reportId), [reportId]);

  // 生成检测报告PDF
  const generatePDF = useCallback(async (
    options: PDFGenerationOptions = {}
  ): Promise<jsPDF> => {
    if (!reportRef.current) {
      throw new Error('检测报告元素未找到');
    }

    setIsGenerating(true);

    try {
      console.log('开始生成检测报告PDF, 元素尺寸:', {
        width: reportRef.current.offsetWidth,
        height: reportRef.current.offsetHeight
      });

      // 等待一段时间确保所有元素渲染完成
      await new Promise(resolve => setTimeout(resolve, 300));

      // 使用html2canvas截取检测报告元素 - 平衡质量和性能
      const canvas = await html2canvas(reportRef.current, {
        scale: 3, // 降低到3倍缩放，平衡质量和性能
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: reportRef.current.offsetWidth,
        height: reportRef.current.offsetHeight,
        logging: true, // 开启日志查看错误
        imageTimeout: 15000
      });

      console.log('检测报告Canvas生成成功, 尺寸:', {
        width: canvas.width,
        height: canvas.height
      });

      // 检查canvas是否有效
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas尺寸为0，可能是元素渲染失败');
      }

      // 创建PDF
      const pdf = new jsPDF({
        orientation: options.orientation || 'portrait',
        unit: 'mm',
        format: options.format || 'a4'
      });

      // 计算图片在PDF中的尺寸
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      console.log('检测报告PDF页面尺寸:', {
        pageWidth: imgWidth,
        pageHeight: pdf.internal.pageSize.getHeight(),
        imgWidth,
        imgHeight
      });

      // 添加图片到PDF - 使用JPEG格式确保兼容性
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      console.log('检测报告图片数据长度:', imageData.length);
      
      // 检查图片数据是否有效
      if (!imageData || imageData.length < 1000) {
        throw new Error('图片数据无效或太小');
      }
      
      pdf.addImage(
        imageData,
        'JPEG',
        0,
        0,
        imgWidth,
        imgHeight
      );

      console.log('检测报告PDF生成完成');
      return pdf;
    } catch (error) {
      console.error('检测报告PDF生成失败:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  // 下载检测报告PDF
  const downloadPDF = useCallback(async (
    filename?: string,
    options?: PDFGenerationOptions
  ): Promise<void> => {
    try {
      const pdf = await generatePDF(options);
      const finalFilename = filename || `test-report-${reportId}.pdf`;
      pdf.save(finalFilename);
    } catch (error) {
      console.error('检测报告PDF下载失败:', error);
      alert('检测报告PDF下载失败，请重试');
    }
  }, [generatePDF, reportId]);

  return {
    reportRef,
    reportId,
    reportUrl,
    generatePDF: () => generatePDF(),
    downloadPDF: (filename?: string) => downloadPDF(filename),
    isGenerating
  };
};